const LAZY_VIDEO_ROOT_MARGIN = '300px';

function loadLazyVideo(video) {
  if (!video?.hasAttribute('data-lazy-video') || video.dataset.lazyLoaded === 'true') {
    return Promise.resolve();
  }

  video.querySelectorAll('source[data-src]').forEach((source) => {
    source.src = source.dataset.src;
    source.removeAttribute('data-src');
  });

  if (video.dataset.src) {
    video.src = video.dataset.src;
    video.removeAttribute('data-src');
  }

  video.dataset.lazyLoaded = 'true';
  video.removeAttribute('data-lazy-video');
  video.load();

  return new Promise((resolve) => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      resolve();
      return;
    }

    video.addEventListener('loadeddata', () => resolve(), { once: true });
  });
}

function enhanceLazyVideo(video) {
  if (video.dataset.lazyEnhanced === 'true') return;

  video.dataset.lazyEnhanced = 'true';
  const nativePlay = HTMLVideoElement.prototype.play.bind(video);

  video.play = async function playLazyVideo() {
    await loadLazyVideo(video);
    return nativePlay();
  };
}

const lazyVideoObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const video = entry.target;
      lazyVideoObserver.unobserve(video);

      loadLazyVideo(video).then(() => {
        if (video.autoplay) {
          video.play().catch(() => {});
        }
      });
    });
  },
  { rootMargin: LAZY_VIDEO_ROOT_MARGIN },
);

function registerLazyVideo(video) {
  if (!video?.hasAttribute('data-lazy-video')) return;

  enhanceLazyVideo(video);
  lazyVideoObserver.observe(video);
}

function observeLazyVideos(root = document) {
  root.querySelectorAll('video[data-lazy-video]').forEach(registerLazyVideo);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => observeLazyVideos());
} else {
  observeLazyVideos();
}

class VideoElement extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.video = this.querySelector('video');
    this.playControlWrapper = this.querySelector('.video__control.play');
    this.pauseControlWrapper = this.querySelector('.video__control.pause');
    this.playControl = this.playControlWrapper?.querySelector('button');
    this.pauseControl = this.pauseControlWrapper?.querySelector('button');

    if (!this.video || !this.playControl || !this.pauseControl) return;

    registerLazyVideo(this.video);
    this.init();
  }

  init() {
    this.video.addEventListener('ended', () => this.showPlayButton());
    this.playControl.addEventListener('click', () => this.playVideo());
    this.pauseControl.addEventListener('click', () => this.pauseVideo());
    this.playControl.addEventListener('keydown', (e) => this.handleKey(e, 'play'));
    this.pauseControl.addEventListener('keydown', (e) => this.handleKey(e, 'pause'));

    this.initialized = true;
  }

  handleKey(event, action) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      if (action === 'play') this.playVideo(true);
      if (action === 'pause') this.pauseVideo(true);
    }
  }

  playVideo(shouldFocus = false) {
    this.video.play();
    this.playControlWrapper.hidden = true;
    this.pauseControlWrapper.hidden = false;

    if (shouldFocus) this.pauseControl.focus();
  }

  pauseVideo(shouldFocus = false) {
    this.video.pause();
    this.playControlWrapper.hidden = false;
    this.pauseControlWrapper.hidden = true;

    if (shouldFocus) this.playControl.focus();
  }

  showPlayButton() {
    this.playControlWrapper.hidden = false;
    this.pauseControlWrapper.hidden = true;
  }
}

customElements.define('video-element', VideoElement);
